import { Icon } from '@/shared/components/ui/icon';
import { cn } from '@/shared/lib/utils';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as React from 'react';

/**
 * Figma 라이브러리 Checkbox (820:72) — 20px · radius 4 · 테두리 1.5px
 * 상태: 기본(grey-400 테두리) · hover(brand-100 채움) · focus(링) · 선택/부분 선택(brand-600 + 흰 기호) · disabled
 * checked="indeterminate"면 가로선을 그린다.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'group/checkbox peer flex size-5 shrink-0 items-center justify-center rounded-4 border-[1.5px] border-grey-400 bg-surface-white text-fg-white outline-none transition-colors',
      'hover:bg-brand-100 focus-visible:shadow-[0_0_0_3px_#aeb6fb]',
      'data-[state=checked]:border-brand-600 data-[state=checked]:bg-brand-600 data-[state=indeterminate]:border-brand-600 data-[state=indeterminate]:bg-brand-600',
      'disabled:pointer-events-none disabled:border-grey-300 disabled:bg-grey-200',
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center">
      <Icon
        name="check-line-16"
        className="group-data-[state=indeterminate]/checkbox:hidden"
      />
      <span className="hidden h-[1.5px] w-3 rounded-full bg-current group-data-[state=indeterminate]/checkbox:block" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

/**
 * Figma Checkbox + Text (820:81) — 체크박스와 글자(15 Regular) 간격 8.
 * textSide="right"면 글자가 앞, 체크박스가 뒤 (Figma Text Side=Right).
 */
const CheckboxField = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    label: React.ReactNode;
    textSide?: 'left' | 'right';
    labelClassName?: string;
  }
>(
  (
    { label, textSide = 'left', className, labelClassName, id, ...props },
    ref,
  ) => {
    const autoId = React.useId();
    const inputId = id ?? autoId;
    return (
      <div
        className={cn(
          'flex items-center gap-2',
          textSide === 'right' && 'flex-row-reverse justify-end',
          className,
        )}
      >
        <Checkbox ref={ref} id={inputId} {...props} />
        <label
          htmlFor={inputId}
          className={cn(
            'cursor-pointer text-body-15-r text-fg-primary peer-disabled:cursor-default peer-disabled:text-fg-disabled',
            labelClassName,
          )}
        >
          {label}
        </label>
      </div>
    );
  },
);
CheckboxField.displayName = 'CheckboxField';

export { Checkbox, CheckboxField };
